import React, { useState } from "react";
import { IoIosPersonAdd } from "react-icons/io";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import { showApiErrors } from "../../../utils/helpers";
import { ArrowLeft, Building2 } from "lucide-react";

const NewBranch = () => {
  const { requestWithAuth } = useAuth();
  const [newBranchData, setNewBranchData] = useState({});
  const [creatingBranch, setCreatingBranch] = useState(false)
  const [errors, setErrors] = useState({});


  const handleChange = (e) => {
    setNewBranchData({ ...newBranchData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };
  const handleBack = () => {
    window.history.back();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newBranchData.name || newBranchData.name.trim() === "") {
      newErrors.name = "Branch name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createBranch = async (e) => {
    e.preventDefault()

    if (!validateForm()) return;

    setCreatingBranch(true)
    const data = JSON.stringify(newBranchData)

    try {
      const response = await requestWithAuth("/company/branches/", 'POST', data, {
        "Content-Type": "application/json"
      });
      setCreatingBranch(false)
      Swal.fire({
        title: "Success",
        icon: "success",
        text: "Branch Created Successfully"
      }).then(() => {
        window.location.href = '/administration/branches'
      })

    } catch (error) {
      showApiErrors(error)
      setCreatingBranch(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Branches</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Company</span>
                <span>/</span>
                <span>Branches</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Create</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Branch</h1>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Building2 className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Branch Information</h2>
                <p className="text-sm text-gray-500">Enter the details for the new branch</p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Branch Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                id="name"
                placeholder="Enter branch name"
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${errors.name
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                  }`}
              />
              {errors.name && (
                <p className="mt-1.5 text-xs text-red-600">{errors.name}</p>
              )}
              {!errors.name && (
                <p className="mt-1.5 text-xs text-gray-500">
                  This will be the official name of the branch
                </p>
              )}
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                id="location"
                placeholder="Enter branch location (optional)"
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone_number"
                id="phone_number"
                placeholder="Enter branch phone number (optional)"
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={4}
                placeholder="Enter branch description (optional)"
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
              />
            </div>
          </div>

          {/* Form Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleBack}
                disabled={creatingBranch}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {!creatingBranch ? (
                <button
                  type="button"
                  onClick={createBranch}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                >
                  Create Branch
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-400 rounded-lg cursor-not-allowed flex items-center gap-2"
                >
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900">About Branches</h3>
              <p className="mt-1 text-sm text-blue-700">
                Branches help you organize your company's different locations. Each branch can have its own staff, inventory, and operations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewBranch;
